import { ArrowLeftOutlined, MailOutlined } from '@ant-design/icons';
import { Alert, Button, Form, Input } from 'antd';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  type LoginEntryPath,
  loginWithEmailCode,
  sendEmailLoginCode,
} from '../api/auth-provider.js';
import './EmailCodeLoginForm.css';

type EmailCodeLoginValues = {
  email: string;
  code: string;
};

type EmailCodeLoginFormProps = {
  backPath?: string;
  loginPath: LoginEntryPath;
  showBack?: boolean;
};

const EMAIL_CODE_SESSION_KEY = 'crest_email_code_login_state';

type EmailCodeSessionState = {
  email?: string;
  expiresAt?: number;
  resendAvailableAt?: number;
};

export function EmailCodeLoginForm({
  backPath = '/login',
  loginPath,
  showBack = false,
}: EmailCodeLoginFormProps) {
  const navigate = useNavigate();
  const [form] = Form.useForm<EmailCodeLoginValues>();
  const [sending, setSending] = useState(false);
  const [loggingIn, setLoggingIn] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    const saved = readEmailCodeSessionState();
    if (saved.email) {
      form.setFieldsValue({ email: saved.email });
    }
    const resendRemaining = remainingSeconds(saved.resendAvailableAt);
    if (resendRemaining > 0) {
      setCountdown(resendRemaining);
    }
    const validRemaining = remainingSeconds(saved.expiresAt);
    if (validRemaining > 0) {
      setNotice(`验证码已发送，请查看邮箱。验证码 ${Math.ceil(validRemaining / 60)} 分钟内有效。`);
    }
  }, [form]);

  useEffect(() => {
    if (countdown <= 0) return;
    const timer = window.setTimeout(() => {
      const saved = readEmailCodeSessionState();
      setCountdown(remainingSeconds(saved.resendAvailableAt));
    }, 1000);
    return () => window.clearTimeout(timer);
  }, [countdown]);

  const handleSendCode = async () => {
    setError(null);
    setNotice(null);
    try {
      const email = await form.validateFields(['email']).then((values) => values.email.trim());
      setSending(true);
      const result = await sendEmailLoginCode(email);
      form.setFieldsValue({ code: '' });
      setCountdown(result.resendAfterSeconds);
      const validMinutes = Math.max(1, Math.floor(result.expiresInSeconds / 60));
      setNotice(`验证码已发送，请查看邮箱。验证码 ${validMinutes} 分钟内有效。`);
      writeEmailCodeSessionState({
        email,
        expiresAt: Date.now() + result.expiresInSeconds * 1000,
        resendAvailableAt: Date.now() + result.resendAfterSeconds * 1000,
      });
    } catch (err) {
      if (isFormValidationError(err)) return;
      setError(readErrorMessage(err, '验证码发送失败'));
    } finally {
      setSending(false);
    }
  };

  const handleLogin = async (values: EmailCodeLoginValues) => {
    setError(null);
    setNotice(null);
    setLoggingIn(true);
    try {
      const result = await loginWithEmailCode({
        email: values.email,
        code: values.code,
        loginEntryPath: loginPath,
      });
      if (result.success) {
        clearEmailCodeSessionState();
        navigate(result.redirectTo ?? '/', { replace: true });
        return;
      }
      setError(result.error.message);
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <div className="crest-login-email-code">
      {showBack && (
        <Button
          className="crest-login-email-back"
          icon={<ArrowLeftOutlined />}
          type="text"
          onClick={() => navigate(backPath)}
        >
          返回
        </Button>
      )}
      <div className="crest-login-email-divider">
        <span>邮箱验证码登录</span>
      </div>
      {error && <Alert className="crest-login-alert" type="error" message={error} showIcon />}
      {notice && <Alert className="crest-login-alert" type="success" message={notice} showIcon />}
      <Form
        autoComplete="off"
        className="crest-login-email-form"
        form={form}
        layout="vertical"
        onValuesChange={(changed: Partial<EmailCodeLoginValues>) => {
          if (!Object.prototype.hasOwnProperty.call(changed, 'email')) return;
          const email = changed.email?.trim() ?? '';
          const saved = readEmailCodeSessionState();
          if (!email) {
            clearEmailCodeSessionState();
            form.setFieldsValue({ code: '' });
            setCountdown(0);
            return;
          }
          if (saved.email && saved.email !== email) {
            writeEmailCodeSessionState({ email });
            form.setFieldsValue({ code: '' });
            setCountdown(0);
            return;
          }
          writeEmailCodeSessionState({ ...saved, email });
        }}
        onFinish={(values) => void handleLogin(values)}
      >
        <Form.Item
          label="邮箱"
          name="email"
          rules={[
            { required: true, message: '请输入邮箱' },
            { type: 'email', message: '邮箱格式不正确' },
            {
              validator: (_, value: string | undefined) =>
                isBaicellsEmail(value)
                  ? Promise.resolve()
                  : Promise.reject(new Error('邮箱必须以 baicells.com 结尾')),
            },
          ]}
        >
          <Input autoComplete="email" placeholder="请输入系统用户邮箱" size="large" />
        </Form.Item>
        <Form.Item
          label="验证码"
          name="code"
          rules={[
            { required: true, message: '请输入验证码' },
            { pattern: /^\d{6}$/, message: '请输入 6 位数字验证码' },
          ]}
        >
          <Input
            autoComplete="off"
            inputMode="numeric"
            maxLength={6}
            placeholder="请输入验证码"
            size="large"
            suffix={
              <Button
                disabled={countdown > 0}
                loading={sending}
                size="small"
                type="link"
                onClick={() => void handleSendCode()}
              >
                {countdown > 0 ? `${countdown}s 后重发` : '获取验证码'}
              </Button>
            }
          />
        </Form.Item>
        <Button
          block
          className="crest-login-email-button"
          htmlType="submit"
          icon={<MailOutlined />}
          loading={loggingIn}
          size="large"
        >
          邮箱登录
        </Button>
      </Form>
    </div>
  );
}

function readErrorMessage(err: unknown, fallback: string): string {
  const error = err as { response?: { data?: { message?: string } }; message?: string };
  return error.response?.data?.message ?? error.message ?? fallback;
}

function isFormValidationError(err: unknown): boolean {
  return Array.isArray((err as { errorFields?: unknown }).errorFields);
}

function isBaicellsEmail(value: string | undefined): boolean {
  return value?.trim().toLowerCase().endsWith('@baicells.com') ?? false;
}

function remainingSeconds(timestamp: number | undefined): number {
  if (!timestamp) return 0;
  return Math.max(0, Math.ceil((timestamp - Date.now()) / 1000));
}

function readEmailCodeSessionState(): EmailCodeSessionState {
  try {
    const raw = sessionStorage.getItem(EMAIL_CODE_SESSION_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as EmailCodeSessionState;
    return {
      email: typeof parsed.email === 'string' ? parsed.email : undefined,
      expiresAt: typeof parsed.expiresAt === 'number' ? parsed.expiresAt : undefined,
      resendAvailableAt:
        typeof parsed.resendAvailableAt === 'number' ? parsed.resendAvailableAt : undefined,
    };
  } catch {
    return {};
  }
}

function writeEmailCodeSessionState(next: EmailCodeSessionState): void {
  try {
    sessionStorage.setItem(EMAIL_CODE_SESSION_KEY, JSON.stringify(next));
  } catch {
    // 登录页状态恢复是体验增强，存储失败不影响登录。
  }
}

function clearEmailCodeSessionState(): void {
  try {
    sessionStorage.removeItem(EMAIL_CODE_SESSION_KEY);
  } catch {
    // ignore
  }
}
